import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Crown, Sparkles, ArrowRight, Building2, Palette, 
  DollarSign, Mail, Copy, Zap, X, Check
} from 'lucide-react';
import { Button } from '../ui/button';
import { useProducerAuth } from '../../context/ProducerAuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const BENEFITS = [
  { icon: Building2, title: 'Hasta 5 Venues', free: '1 venue', premium: '5 venues' },
  { icon: Palette, title: 'Todos los Estilos', free: 'Solo Clásico', premium: '9 estilos premium' },
  { icon: DollarSign, title: 'Comisión Reducida', free: '15%', premium: '10%' },
  { icon: Mail, title: 'Emails Marketing', free: '0/mes', premium: '1,000/mes' },
  { icon: Copy, title: 'Duplicar Eventos', free: 'No', premium: 'Sí' },
  { icon: Zap, title: 'Liquidación Express', free: 'No', premium: 'Hasta 50%' },
];

// Small banner for sidebar or dashboard
export const SubscriptionBannerSmall = () => {
  const { isPremium, subscription, getPaymentLink } = useProducerAuth();
  const [showModal, setShowModal] = useState(false);

  if (isPremium()) {
    return (
      <div className="bg-gradient-to-r from-[#D4AF37]/20 to-amber-600/20 border border-[#D4AF37]/30 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
            <Crown className="text-black" size={20} />
          </div>
          <div>
            <p className="font-bold text-[#D4AF37]">Super Productora</p>
            <p className="text-xs text-zinc-400">Plan activo</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="w-full bg-gradient-to-r from-zinc-900 to-zinc-800 border border-zinc-700 hover:border-[#D4AF37]/50 rounded-xl p-4 transition-all group"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-zinc-800 group-hover:bg-[#D4AF37]/20 rounded-lg flex items-center justify-center transition-colors">
            <Crown className="text-[#D4AF37]" size={20} />
          </div>
          <div className="text-left flex-1">
            <p className="font-bold text-white group-hover:text-[#D4AF37] transition-colors">
              Super Productora
            </p>
            <p className="text-xs text-zinc-500">Desbloquea todas las funciones</p>
          </div>
          <ArrowRight className="text-zinc-600 group-hover:text-[#D4AF37] transition-colors" size={16} />
        </div>
      </button>

      <SubscriptionModal open={showModal} onClose={() => setShowModal(false)} />
    </>
  );
};

// Full banner for dashboard
export const SubscriptionBannerFull = () => {
  const { isPremium, subscription, getPaymentLink } = useProducerAuth();
  const [showModal, setShowModal] = useState(false);

  if (isPremium()) {
    return (
      <div className="bg-gradient-to-r from-[#D4AF37]/10 via-amber-600/10 to-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#D4AF37] to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-[#D4AF37]/20">
              <Crown className="text-black" size={28} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                Super Productora
                <Sparkles className="text-[#D4AF37]" size={18} />
              </h3>
              <p className="text-zinc-400">
                Disfrutá de todos los beneficios premium
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-zinc-500">Estado</p>
            <p className="text-lg font-bold text-green-400">Activo</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 border border-zinc-700 rounded-2xl p-6"
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#D4AF37]/20 to-amber-600/20 border border-[#D4AF37]/30 rounded-xl flex items-center justify-center">
              <Crown className="text-[#D4AF37]" size={28} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                Pasá a Super Productora
                <Sparkles className="text-[#D4AF37]" size={18} />
              </h3>
              <p className="text-zinc-400 mt-1 max-w-md">
                Desbloqueá venues ilimitados, estilos premium, comisiones reducidas y mucho más.
              </p>
              <div className="flex flex-wrap gap-3 mt-3">
                {['5 Venues', '9 Estilos', '10% Comisión', 'Emails'].map((tag) => (
                  <span key={tag} className="text-xs bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-1 rounded-full border border-[#D4AF37]/20">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          <Button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-[#D4AF37] to-amber-600 hover:from-[#C4A030] hover:to-amber-700 text-black font-bold px-6 py-5 rounded-xl shadow-lg shadow-[#D4AF37]/20 hover:shadow-[#D4AF37]/40 transition-all"
          >
            <Crown size={18} className="mr-2" />
            Ver Planes
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </motion.div>

      <SubscriptionModal open={showModal} onClose={() => setShowModal(false)} />
    </>
  );
};

// Modal with full details
const SubscriptionModal = ({ open, onClose }) => {
  const { subscription, getPaymentLink } = useProducerAuth();
  const planInfo = subscription?.plan_info || {};

  const handleSubscribe = () => {
    const link = getPaymentLink();
    if (link) {
      window.open(link, '_blank');
    } else {
      alert('El link de pago no está configurado. Contacta al administrador.');
    }
  };

  const formatPrice = (price) => {
    if (!price) return '---';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: planInfo.currency || 'ARS',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-white flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-amber-600 rounded-xl flex items-center justify-center">
              <Crown className="text-black" size={24} />
            </div>
            Super Productora
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Price */}
          <div className="text-center py-4">
            <p className="text-zinc-500 mb-2">Suscripción mensual</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-5xl font-black bg-gradient-to-r from-[#D4AF37] to-amber-500 bg-clip-text text-transparent">
                {formatPrice(planInfo.price_monthly)}
              </span>
              <span className="text-zinc-500">/mes</span>
            </div>
          </div>

          {/* Benefits comparison */}
          <div className="space-y-3">
            <h4 className="font-medium text-white text-center mb-4">Comparación de planes</h4>
            
            <div className="grid grid-cols-3 gap-2 text-sm mb-2 px-4">
              <div></div>
              <div className="text-center text-zinc-500">Gratis</div>
              <div className="text-center text-[#D4AF37] font-medium">Premium</div>
            </div>
            
            {BENEFITS.map((benefit, idx) => (
              <div 
                key={idx}
                className="grid grid-cols-3 gap-2 items-center p-3 bg-zinc-800/50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <benefit.icon className="text-zinc-500" size={16} />
                  <span className="text-white text-sm">{benefit.title}</span>
                </div>
                <div className="text-center text-zinc-500 text-sm">{benefit.free}</div>
                <div className="text-center text-[#D4AF37] text-sm font-medium">{benefit.premium}</div>
              </div>
            ))}
          </div>

          {/* Additional benefits */}
          <div className="bg-zinc-800/30 rounded-xl p-4">
            <h4 className="font-medium text-white mb-3 flex items-center gap-2">
              <Sparkles className="text-[#D4AF37]" size={16} />
              También incluye
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                'Duplicar eventos con un click',
                'Duplicar venues/planos',
                'Soporte prioritario',
                'Estadísticas avanzadas'
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-zinc-400">
                  <Check className="text-green-500" size={14} />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t border-zinc-800">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-zinc-700"
          >
            Quizás después
          </Button>
          <Button
            onClick={handleSubscribe}
            className="flex-1 bg-gradient-to-r from-[#D4AF37] to-amber-600 hover:from-[#C4A030] hover:to-amber-700 text-black font-bold"
          >
            <Crown size={18} className="mr-2" />
            Suscribirme Ahora
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionBannerFull;
